import { FC, useCallback, useState } from 'react';
import { Image, Input, Modal, Space, Typography, message } from 'antd';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { useTranslation } from 'react-i18next';
import { SettingsKeys } from '@medicusunion/front-utils';
import { useAuthDispatchContext } from 'providers/Auth';
import { updateSettings } from 'services/api/profile';

import { showRestError } from 'utils/showErrorMessage';
import { StyledList, StyledToken } from './styled';
import { instance } from 'utils/api';

const { Title, Paragraph } = Typography;

interface IProps {
  onCancel: () => void;
}

const AuthenticationModal: FC<IProps> = ({ onCancel }) => {
  // translations
  const { t } = useTranslation();
  // context
  const { authentication } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();
  const { refetchUserData } = useAuthDispatchContext();
  // states
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  const closeModal = useCallback(
    (cancel: boolean) => {
      if (cancel) {
        onCancel();
        // merged server-side; disarm the 2FA flag only
        updateSettings({ [SettingsKeys.auth2fa]: false })
          .then(() => refetchUserData())
          .catch(err => showRestError(err));
      }

      toggleModal('authentication', false);
      setCode('');
    },
    [toggleModal, refetchUserData, onCancel]
  );

  const onConfirm = useCallback(async () => {
    if (code) {
      // instance handles auth + 401 refresh; the BE arms 2FA on the first
      // successful verify, updateSettings persists the flag.
      await instance
        .post('/auth/otp/verify-authenticated', {
          otp: code
        })
        .then(() => {
          updateSettings({ [SettingsKeys.auth2fa]: true })
            .then(() => refetchUserData())
            .catch(err => showRestError(err));
          message.success(t('profile.2fa.success'));
          closeModal(false);
        })
        .catch(err => {
          showRestError(err);
        });
    } else {
      setError(true);
    }
  }, [closeModal, code, t, refetchUserData]);

  return (
    <Modal
      open={authentication?.visible}
      onCancel={() => closeModal(true)}
      onOk={onConfirm}
      confirmLoading={false}
      title={t('profile.2fa.title')}
    >
      <Space direction="vertical" size={16}>
        <div>
          <Title level={4}>{t('profile.2fa.configuring')}</Title>
          <StyledList>
            <li>{t('profile.2fa.install_app')}</li>
            <li>{t('profile.2fa.add')}</li>
            <li>{t('profile.2fa.select_scan')}</li>
          </StyledList>
        </div>
        <div>
          <Title level={4}>{t('profile.2fa.scan_code')}</Title>
          <Image
            width={200}
            height={200}
            preview={false}
            src={authentication.params?.qr}
          />
        </div>
        <div>
          <Title level={4}>{t('profile.2fa.enter_code')}</Title>
          <StyledToken copyable code>
            {authentication.params?.secretCode}
          </StyledToken>
        </div>
        <div>
          <Title level={4}>{t('profile.2fa.verify_code')}</Title>
          <Paragraph>{t('profile.2fa.verify')}</Paragraph>
          <Input
            value={code}
            onChange={e => setCode(e.target.value)}
            status={error ? 'error' : ''}
          />
        </div>
      </Space>
    </Modal>
  );
};

export default AuthenticationModal;
